@if (layout.mobileDrawerOpen()) {
  <!-- Backdrop -->
  <div class="drawer-backdrop" (click)="onBackdropClick($event)" (keydown.escape)="close()"></div>
}

<!-- Drawer panel (always in DOM for transition) -->
<div class="drawer-panel" [class.open]="layout.mobileDrawerOpen()">

  <!-- Header: tenant name + optional switcher + close -->
  <div class="shrink-0 border-b" style="border-color: var(--nexus-border)">
    <div class="flex items-center justify-between px-4 py-4">
      @if (activeTenant.canSwitch()) {
        <button type="button"
                (click)="tenantOpen.update(v => !v)"
                class="flex items-center gap-2.5 min-w-0 rounded-lg px-2 py-1.5 -mx-2 transition-colors hover:bg-white/5">
          <div class="flex flex-col min-w-0 text-left">
            <span class="text-[10px] uppercase tracking-widest font-mono"
                  style="color: var(--nexus-text-dim)">Tenant</span>
            <span class="text-[15px] font-semibold truncate"
                  style="color: var(--nexus-text)">{{ tenantName() }}</span>
          </div>
          <lucide-icon [img]="tenantOpen() ? ChevronUp : ChevronDown" class="w-4 h-4 shrink-0"
                       style="color: var(--nexus-cyan)"/>
        </button>
      } @else {
        <div class="flex flex-col min-w-0">
          <span class="text-[10px] uppercase tracking-widest font-mono"
                style="color: var(--nexus-text-dim)">Tenant</span>
          <span class="text-[15px] font-semibold truncate"
                style="color: var(--nexus-text)">{{ tenantName() }}</span>
        </div>
      }
      <button type="button" (click)="close()"
              class="w-9 h-9 flex items-center justify-center rounded-lg transition-colors hover:bg-white/5 ml-2 shrink-0"
              aria-label="Close menu">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"
             style="color: var(--nexus-text-muted)">
          <path d="M18 6 6 18M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <!-- Inline tenant list (only when multi-tenant + open) -->
    @if (activeTenant.canSwitch() && tenantOpen()) {
      <div class="px-3 pb-3"
           style="background: var(--nexus-surface); border-top: 1px solid var(--nexus-border); max-height: 200px; overflow-y: auto;">
        @for (t of activeTenant.tenants(); track t.id) {
          <button type="button"
                  (click)="switchTenant(t.id)"
                  class="w-full flex items-center justify-between px-3 py-2.5 mt-1.5 rounded-lg text-sm transition-colors"
                  [style]="t.id === activeTenant.activeId()
                    ? 'background:rgba(0,229,255,0.1);color:var(--nexus-cyan);border:1px solid rgba(0,229,255,0.2)'
                    : 'color:var(--nexus-text);border:1px solid transparent'">
            <span class="font-medium truncate">{{ t.name }}</span>
            @if (t.id === activeTenant.activeId()) {
              <span class="nx-dot-cyan shrink-0 ml-2"></span>
            }
          </button>
        }
        <a routerLink="/me/tenants" (click)="close()"
           class="flex items-center px-3 py-2 mt-2 text-xs font-medium rounded-md transition-colors hover:bg-white/5"
           style="color:var(--nexus-text-muted)">
          Manage all tenants →
        </a>
      </div>
    }
  </div>

  <!-- Navigation -->
  <nav class="flex-1 py-3 overflow-y-auto">

    <!-- ── TENANT sections (hidden for platform admin) ── -->
    @if (!auth.isPlatformAdmin()) {

      <!-- OVERVIEW (everyone) -->
      <div class="px-3 mb-1">
        <span class="nx-label px-2">Overview</span>
        <a routerLink="/dashboard" routerLinkActive="nav-active" (click)="close()"
           class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
            <rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/>
            <rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/>
          </svg>
          Dashboard
        </a>
        <a routerLink="/projects" routerLinkActive="nav-active" (click)="close()"
           class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
            <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          </svg>
          Projects
        </a>
      </div>

      <!-- BILLING (billing_access or owner) -->
      @if (canSeeBilling()) {
        <div class="px-3 mt-3 mb-1">
          <span class="nx-label px-2">Billing</span>
          <a routerLink="/billing" routerLinkActive="nav-active" (click)="close()"
             class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
              <rect x="3" y="6" width="18" height="13" rx="2"/><path d="M3 10h18M7 15h4"/>
            </svg>
            Billing
          </a>
          <a routerLink="/pricing" routerLinkActive="nav-active" (click)="close()"
             class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
              <path d="M12 3l9 5-9 5-9-5 9-5z"/><path d="M3 12l9 5 9-5M3 17l9 5 9-5"/>
            </svg>
            Plans
          </a>
        </div>
      }

      <!-- COMPANY -->
      @if (canSeeAuditLogs() || canSeeSecurity() || canSeeMembers() || canSeeSettings()) {
        <div class="px-3 mt-3 mb-1">
          <span class="nx-label px-2">Company</span>
          @if (canSeeAuditLogs()) {
            <a routerLink="/audit-logs" routerLinkActive="nav-active" (click)="close()"
               class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
                <path d="M4 4h16v16H4z"/><path d="M8 9h8M8 13h8M8 17h5"/>
              </svg>
              Audit Logs
            </a>
          }
          @if (canSeeSecurity()) {
            <a routerLink="/security" routerLinkActive="nav-active" (click)="close()"
               class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/>
              </svg>
              Security
            </a>
          }
          @if (canSeeMembers() && tenantMembersLink(); as membersLink) {
            <a [routerLink]="membersLink" routerLinkActive="nav-active" (click)="close()"
               class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              Members
            </a>
          }
          @if (canSeeSettings() && tenantSettingsLink(); as settingsLink) {
            <a [routerLink]="settingsLink" routerLinkActive="nav-active" (click)="close()"
               class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
              </svg>
              Settings
            </a>
          }
        </div>
      }

      <!-- HELP (everyone) -->
      <div class="px-3 mt-3 mb-1">
        <span class="nx-label px-2">Help</span>
        <a routerLink="/whats-new" routerLinkActive="nav-active" (click)="close()"
           class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          What's New
          @if (announcementsService.unreadCount() > 0) {
            <span class="ml-auto w-2 h-2 rounded-full shrink-0" style="background: var(--nexus-cyan)"></span>
          }
        </a>
        <a [href]="docsUrl" target="_blank" rel="noopener" (click)="close()"
           class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
            <circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 4M12 17h.01"/>
          </svg>
          Help <span class="ml-1 opacity-60">↗</span>
        </a>
        <a routerLink="/feedback" routerLinkActive="nav-active" (click)="close()"
           class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
          <lucide-icon [img]="MyFeedbackIcon" class="w-4 h-4 shrink-0" />
          My Feedback
        </a>
      </div>

    } <!-- end tenant sections -->

    <!-- ── PLATFORM ADMIN sections ── -->
    @if (auth.isPlatformAdmin()) {

      <!-- PLATFORM -->
      <div class="px-3 mb-1">
        <span class="nx-label px-2">Platform</span>
        <a routerLink="/platform-admin/platform" routerLinkActive="nav-active" (click)="close()"
           class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
            <rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/>
            <rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/>
          </svg>
          Overview
        </a>
        <a routerLink="/platform/alerts" routerLinkActive="nav-active" (click)="close()"
           class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          Alerts
        </a>
        <a routerLink="/platform-admin/tiers" routerLinkActive="nav-active" (click)="close()"
           class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
            <path d="M12 3l9 5-9 5-9-5 9-5z"/><path d="M3 12l9 5 9-5M3 17l9 5 9-5"/>
          </svg>
          Tiers
        </a>
        <a routerLink="/platform-admin/promo-codes" routerLinkActive="nav-active" (click)="close()"
           class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
            <path d="M2 9a3 3 0 1 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 1 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/>
            <path d="M9 9h.01"/><path d="m15 9-6 6"/><path d="M15 15h.01"/>
          </svg>
          Promo codes
        </a>
        <a routerLink="/platform-admin/announcements" routerLinkActive="nav-active" (click)="close()"
           class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
            <path d="M3 11l19-9-9 19-2-8-8-2z"/>
          </svg>
          Announcements
        </a>
        <a routerLink="/platform-admin/feedback" routerLinkActive="nav-active" (click)="close()"
           class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
            <path d="M11.7 3H5a2 2 0 0 0-2 2v14l4-4h12a2 2 0 0 0 2-2v-2.7"/>
            <circle cx="19" cy="5" r="3"/>
          </svg>
          Feedback
          @if (platformSettingsService.adminUnreadCount() > 0) {
            <span class="ml-auto w-2 h-2 rounded-full shrink-0"
                  style="background: var(--nexus-cyan); box-shadow: 0 0 0 1.5px var(--nexus-panel)"></span>
          }
        </a>
        <a routerLink="/platform-admin/platform-settings" routerLinkActive="nav-active" (click)="close()"
           class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
            <line x1="21" y1="4" x2="14" y2="4"/><line x1="10" y1="4" x2="3" y2="4"/>
            <circle cx="12" cy="4" r="2"/>
            <line x1="21" y1="12" x2="12" y2="12"/><line x1="8" y1="12" x2="3" y2="12"/>
            <circle cx="10" cy="12" r="2"/>
            <line x1="21" y1="20" x2="16" y2="20"/><line x1="12" y1="20" x2="3" y2="20"/>
            <circle cx="14" cy="20" r="2"/>
          </svg>
          Platform Settings
          @if (platformSettingsService.pendingRestart()) {
            <span class="ml-auto w-2 h-2 rounded-full shrink-0"
                  style="background: var(--nexus-yellow); box-shadow: 0 0 0 1.5px var(--nexus-panel)"
                  title="Settings pending restart"></span>
          }
        </a>
      </div>

      <!-- ADMINISTRATION -->
      <div class="px-3 mt-3 mb-1">
        <span class="nx-label px-2">Administration</span>
        <a routerLink="/platform-admin/tenants" routerLinkActive="nav-active" (click)="close()"
           class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
            <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18"/>
            <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/>
            <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/>
            <path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/>
          </svg>
          Tenants
        </a>
        <a routerLink="/projects" routerLinkActive="nav-active" (click)="close()"
           class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
            <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          </svg>
          Projects
        </a>
        <a routerLink="/users" routerLinkActive="nav-active" (click)="close()"
           class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          Users
        </a>
        <a routerLink="/platform-admin/access-requests" routerLinkActive="nav-active" (click)="close()"
           class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5"
           style="color: var(--nexus-yellow)">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0"
               style="color: var(--nexus-yellow)">
            <rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/>
          </svg>
          Access Requests
          @if (platformAdmin.unreadCount() > 0) {
            <span class="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style="background:rgba(245,158,11,0.15);color:var(--badge-amber-fg);border:1px solid rgba(245,158,11,0.3)">
              {{ platformAdmin.unreadCount() }}
            </span>
          }
        </a>
      </div>

      <!-- HELP (platform admin) -->
      <div class="px-3 mt-3 mb-1">
        <span class="nx-label px-2">Help</span>
        <a [href]="docsUrl" target="_blank" rel="noopener" (click)="close()"
           class="nav-item flex items-center gap-2.5 px-2 py-2.5 rounded-md text-xs font-medium transition-all mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0">
            <circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 4M12 17h.01"/>
          </svg>
          Help <span class="ml-1 opacity-60">↗</span>
        </a>
      </div>

    } <!-- end platform admin sections -->

  </nav>

  <!-- Footer: user info + Settings + Log out -->
  <div class="border-t px-4 pt-4 pb-6 shrink-0" style="border-color: var(--nexus-border)">
    <div class="flex items-center gap-3 mb-3 min-w-0">
      <div class="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
           style="background:rgba(0,229,255,0.12);color:var(--nexus-cyan);border:1px solid rgba(0,229,255,0.25)">
        {{ initials() }}
      </div>
      <div class="flex flex-col min-w-0">
        <span class="text-sm font-semibold truncate" style="color: var(--nexus-text)">{{ displayName() }}</span>
        <span class="text-[11px] font-mono truncate" style="color: var(--nexus-text-dim)">{{ userEmail() }}</span>
      </div>
    </div>
    @if (!auth.isPlatformAdmin()) {
      <button type="button" (click)="openFeedback()"
              class="w-full flex items-center gap-2 px-3 py-2 mb-2 rounded-lg text-xs font-semibold transition-all"
              style="color:var(--nexus-yellow);background:rgba(245,158,11,0.15);border:1px solid rgba(245,158,11,0.55)">
        <lucide-icon [img]="FeedbackIcon" class="w-3.5 h-3.5 shrink-0" />
        Send Feedback
      </button>
    }
    <div class="flex gap-2">
      <a routerLink="/settings" (click)="close()"
         class="flex-1 text-center text-xs font-medium px-3 py-2 rounded-lg transition-colors hover:bg-white/5"
         style="color:var(--nexus-text-muted);border:1px solid var(--nexus-border)">
        Settings
      </a>
      <button type="button" (click)="logout()"
              class="flex-1 text-xs font-medium px-3 py-2 rounded-lg transition-all hover:bg-red-500/10"
              style="color:var(--nexus-red);border:1px solid rgba(255,51,102,0.3)">
        Log out
      </button>
    </div>
  </div>

</div>

@if (feedbackOpen()) {
  <app-feedback-modal #feedbackModal />
}
